import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { feedbackService } from '@/services/feedback.service';
import type { AnonymousFeedbackItem } from '@/types/feedback';
import { StarRating, FEEDBACK_MAX_STARS } from './StarRating';
import { Star } from 'lucide-react';

const RATING_LABELS: Record<string, string> = {
  rating_overall: 'การเปิดหน้ากุรอานและการเลื่อนอ่าน ทำได้ลื่นไหลเพียงใด?',
  rating_settings: 'การปรับแต่ง (เช่น ขนาดอักษร/ตัจวีด) หาปุ่มง่ายและสะดวกไหม?',
  rating_translation: 'การเปิดดูคำแปลในแต่ละอายะฮ์ ทำได้รวดเร็วและไม่ขัดจังหวะไหม?',
  rating_save_progress: 'ขั้นตอนการ "บันทึกความก้าวหน้า" ใช้เวลาน้อยและสะดวกเพียงใด?',
  rating_recalc_feature:
    'ฟีเจอร์ "คำนวณแผนการอ่าน" ช่วยให้รักษาเป้าหมายได้ดีขึ้นไหม?',
  rating_read_more:
    'แอปนี้ทำให้พี่น้องอ่านกุรอาน "บ่อยขึ้น/สม่ำเสมอขึ้น" กว่าเดิมไหม?',
};

const LABEL_DIFFICULT =
  'พี่น้องเจอจุดที่ใช้งานยาก หรือสับสนตรงไหนบ้างหรือไม่?';
const LABEL_FEATURES =
  'มีฟีเจอร์อะไรที่พี่น้องอยากให้เพิ่มเข้ามา และพัฒนาให้ดีขึ้นในเวอร์ชันถัดไป?';

function EmptyFeedbackIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-10 w-10 text-muted-foreground/70"
    >
      <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
      <path d="M9 10h6" />
      <path d="M9 13h3" />
    </svg>
  );
}

function CompactStars({ value }: Readonly<{ value: number }>) {
  const v = Math.min(value, FEEDBACK_MAX_STARS);
  return (
    <div
      className="inline-flex flex-col items-center gap-0.5"
      aria-label={`คะแนน ${v} จาก ${FEEDBACK_MAX_STARS}`}
    >
      <div className="flex gap-0">
        {Array.from({ length: FEEDBACK_MAX_STARS }, (_, index) => {
          const starValue = index + 1;
          const filled = v >= starValue;
          return (
            <Star
              key={starValue}
              className={`h-3.5 w-3.5 ${filled ? 'fill-amber-400 text-amber-500' : 'text-muted-foreground/30'}`}
            />
          );
        })}
      </div>
    </div>
  );
}

export function AnonymousFeedbackList() {
  const { data: list = [], isLoading, error } = useQuery({
    queryKey: ['feedback', 'anonymous-list'],
    queryFn: () => feedbackService.listAnonymousFeedback(),
  });
  const [showAll, setShowAll] = useState(false);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">ความเห็นจากผู้ใช้คนอื่น</CardTitle>
          <CardDescription>ไม่เปิดเผยตัวตน</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="mt-2 h-24 w-full rounded-lg" />
          <Skeleton className="mt-2 h-24 w-full rounded-lg" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">ความเห็นจากผู้ใช้คนอื่น</CardTitle>
          <CardDescription>ไม่เปิดเผยตัวตน</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            โหลดความเห็นไม่สำเร็จ กรุณาลองใหม่ภายหลัง
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!list.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">ความเห็นจากผู้ใช้คนอื่น</CardTitle>
          <CardDescription>ไม่เปิดเผยตัวตน</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border/60 bg-muted/20 px-6 py-8 text-center">
            <EmptyFeedbackIcon />
            <p className="mt-3 text-sm font-medium text-foreground">
              ยังไม่มี Feedback
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              เป็นคนแรกที่ส่งความเห็นเข้ามาได้เลย
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getHighestRating = (item: AnonymousFeedbackItem) =>
    Math.max(
      item.rating_overall,
      item.rating_settings,
      item.rating_translation,
      item.rating_save_progress,
      item.rating_recalc_feature,
      item.rating_read_more
    );

  const PREVIEW_COUNT = 2;
  const visibleList = showAll ? list : list.slice(0, PREVIEW_COUNT);
  const hiddenCount = Math.max(0, list.length - PREVIEW_COUNT);
  let toggleLabel = 'อ่านเพิ่มเติม';
  if (showAll) {
    toggleLabel = 'ย่อ';
  } else if (hiddenCount > 0) {
    toggleLabel = `อ่านเพิ่มเติม (อีก ${hiddenCount} คน)`;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">ความเห็นจากผู้ใช้คนอื่น</CardTitle>
        <CardDescription>ไม่เปิดเผยตัวตน</CardDescription>
      </CardHeader>
      <CardContent>
        <Accordion type="single" collapsible className="w-full">
          {visibleList.map((item: AnonymousFeedbackItem, index: number) => (
            <AccordionItem key={`${item.anonymous_label}-${index}`} value={`item-${index}`}>
              <AccordionTrigger className="hover:no-underline py-3">
                <div className="flex w-full flex-col gap-2 text-left">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar className="h-9 w-9 shrink-0">
                        <AvatarFallback className="bg-primary/20 text-primary font-medium">
                          {item.anonymous_label}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium truncate">
                        ผู้ใช้ {item.anonymous_label}
                      </span>
                    </div>
                    <div className="inline-flex shrink-0 rounded-full bg-muted/60 px-2 py-1.5">
                      <CompactStars value={getHighestRating(item)} />
                    </div>
                  </div>
                  {item.feedback_difficult?.trim() && (
                    <p
                      className="text-sm text-muted-foreground pr-2"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {item.feedback_difficult}
                    </p>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-4 pt-0">
                <div className="space-y-4 pl-1">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">
                      {RATING_LABELS.rating_overall}
                    </p>
                    <StarRating value={item.rating_overall} readonly showScaleLabels />
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">
                      {RATING_LABELS.rating_settings}
                    </p>
                    <StarRating value={item.rating_settings} readonly showScaleLabels />
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">
                      {RATING_LABELS.rating_translation}
                    </p>
                    <StarRating value={item.rating_translation} readonly showScaleLabels />
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">
                      {RATING_LABELS.rating_save_progress}
                    </p>
                    <StarRating value={item.rating_save_progress} readonly showScaleLabels />
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">
                      {RATING_LABELS.rating_recalc_feature}
                    </p>
                    <StarRating value={item.rating_recalc_feature} readonly showScaleLabels />
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">
                      {RATING_LABELS.rating_read_more}
                    </p>
                    <StarRating value={item.rating_read_more} readonly showScaleLabels />
                  </div>
                  {item.feedback_difficult?.trim() && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">
                        {LABEL_DIFFICULT}
                      </p>
                      <p className="text-sm text-foreground rounded-md bg-muted/50 p-2">
                        {item.feedback_difficult}
                      </p>
                    </div>
                  )}
                  {item.feedback_features?.trim() && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">
                        {LABEL_FEATURES}
                      </p>
                      <p className="text-sm text-foreground rounded-md bg-muted/50 p-2">
                        {item.feedback_features}
                      </p>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        {hiddenCount > 0 && (
          <div className="mt-4 flex justify-center">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAll((v) => !v)}
            >
              {toggleLabel}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
